/**
 * @package   EGI / Frontend — home
 * @author    Frontend Specialist (AI) for Fabio Cherici
 * @version   1.0.0 (M-EGI-264 FASE B2)
 * @date      2026-06-11
 * @purpose   Isola di paging dei caroselli egi /home (flag HOME_LAZY_SECTIONS): pagine
 *            di N card — N = floor(trackWidth / (card+gap)) misurato dal DOM, min 1
 *            (gap da computed style: `space-x-4` → margin-left sibling, fallback 16).
 *            Pagina non nel DOM → fetch {endpoint}?offset=<nel-DOM>&limit=min(N,12)
 *            (HTML same-origin Blade-escapato, NON input utente) → inject → scroll.
 *            Pallini ceil(total/N) su scroll(rAF)/resize(debounce). Un fetch in volo:
 *            aria-busy + frecce aria-disabled (mai `disabled` → focus mai rubato,
 *            WCAG 2.2 §2.4.7 A). Errore → role="status" i18n + retry. Card iniettate
 *            ereditano i listener delegati su document (egi-card-interactions.ts).
 *            Flag OFF → nessun [data-carousel-section] → no-op totale.
 */

interface PagerLabels {
    loadError: string;
    retry: string;
    goToPage: string;
}

const LABEL_KEYS: ReadonlyArray<keyof PagerLabels> = ['loadError', 'retry', 'goToPage'];

function isPagerLabels(value: unknown): value is PagerLabels {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>; // narrow post-guard: solo lettura chiavi
    return LABEL_KEYS.every((key) => typeof record[key] === 'string');
}

/** Legge le stringhe i18n emesse UNA volta dal Blade (@once, #carousel-pager-i18n). */
function readLabels(): PagerLabels | null {
    const el = document.getElementById('carousel-pager-i18n');
    if (!el || !el.textContent) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(el.textContent);
        if (isPagerLabels(parsed)) {
            return parsed;
        }
    } catch {
        // JSON malformato → isola spenta: il track resta scrollabile nativamente.
    }
    return null;
}

function initCarousel(section: HTMLElement, labels: PagerLabels): void {
    const trackQ = section.querySelector<HTMLElement>('[data-carousel-track]');
    const prevQ = section.querySelector<HTMLElement>('[data-carousel-prev]');
    const nextQ = section.querySelector<HTMLElement>('[data-carousel-next]');
    const dotsQ = section.querySelector<HTMLElement>('[data-carousel-dots]');
    const endpoint = section.dataset.carouselEndpoint ?? '';
    let total = Number(section.dataset.carouselTotal);

    if (!trackQ || !prevQ || !nextQ || !dotsQ || endpoint === '' || !Number.isFinite(total) || total <= 0) {
        return; // markup incompleto → isola spenta su questa sezione (scroll nativo intatto)
    }

    // Re-bind non-null: il narrowing dei const NON propaga nelle funzioni hoisted sotto.
    const [track, prev, next, dots]: HTMLElement[] = [trackQ, prevQ, nextQ, dotsQ];

    let cardStep = 1; // larghezza card + gap (px), misurata dal DOM
    let perPage = 1; // N = card visibili per pagina
    let busy = false; // un solo fetch in volo (contratto CEO)
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* Regione live PERSISTENTE (vuota): esiste prima dell'errore, così il
       popolamento è un change event annunciato dagli screen reader (aria-live). */
    const status = document.createElement('div');
    status.setAttribute('role', 'status');
    status.className = 'mt-3 flex min-h-6 items-center justify-center gap-3 text-sm';
    track.insertAdjacentElement('afterend', status);

    function measure(): void {
        const first = track.children[0];
        if (!(first instanceof HTMLElement)) {
            return;
        }
        const cardWidth = first.getBoundingClientRect().width;
        const second = track.children[1];
        let gap = second instanceof HTMLElement ? Number.parseFloat(getComputedStyle(second).marginLeft) : Number.NaN;
        if (!Number.isFinite(gap) || gap <= 0) {
            gap = Number.parseFloat(getComputedStyle(track).columnGap) || 16;
        }
        cardStep = Math.max(1, cardWidth + gap);
        perPage = Math.max(1, Math.floor(track.clientWidth / cardStep)); // formula CEO, min 1
    }

    const pageWidth = (): number => perPage * cardStep;
    const pageCount = (): number => Math.max(1, Math.ceil(total / perPage));
    const maxScroll = (): number => track.scrollWidth - track.clientWidth;

    function currentPage(): number {
        if (track.scrollLeft >= maxScroll() - 2) {
            return pageCount() - 1; // ultima pagina parziale: il clamp dello scroll non allinea al multiplo
        }
        return Math.min(pageCount() - 1, Math.max(0, Math.round(track.scrollLeft / pageWidth())));
    }

    function setArrowState(arrow: HTMLElement, disabled: boolean): void {
        arrow.setAttribute('aria-disabled', String(disabled));
        arrow.style.opacity = disabled ? '0.5' : '1';
    }

    function updateUI(): void {
        const page = currentPage();
        setArrowState(prev, busy || page <= 0);
        setArrowState(next, busy || page >= pageCount() - 1);
        dots.querySelectorAll<HTMLElement>('button').forEach((dot, index) => {
            const active = index === page;
            dot.classList.toggle('bg-purple-500', active);
            dot.classList.toggle('scale-125', active);
            dot.classList.toggle('bg-gray-600', !active);
            if (active) {
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.removeAttribute('aria-current');
            }
        });
    }

    function renderDots(): void {
        dots.replaceChildren();
        for (let i = 0; i < pageCount(); i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className =
                'h-2 w-2 rounded-full bg-gray-600 transition-all duration-300 hover:bg-purple-500 ' +
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400';
            // i18n atomico (convenzione FlorenceEGI): chiave + ' ' + numero
            dot.setAttribute('aria-label', labels.goToPage + ' ' + String(i + 1));
            dot.addEventListener('click', () => {
                void goToPage(i);
            });
            dots.appendChild(dot);
        }
        updateUI();
    }

    function clearStatus(): void {
        status.replaceChildren();
    }

    function showError(targetPage: number): void {
        clearStatus();
        const message = document.createElement('span');
        message.className = 'text-red-300';
        message.textContent = labels.loadError;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className =
            'rounded px-3 py-1.5 font-semibold text-white underline underline-offset-2 hover:text-purple-300 ' +
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400';
        retry.textContent = labels.retry;
        retry.addEventListener('click', () => {
            clearStatus();
            void goToPage(targetPage);
        });
        status.append(message, retry);
    }

    async function fetchPage(offset: number, limit: number): Promise<number> {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('offset', String(offset));
        url.searchParams.set('limit', String(limit));
        const response = await fetch(url.toString(), {
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
        });
        if (!response.ok) {
            throw new Error('HTTP ' + String(response.status));
        }
        const headerTotal = Number(response.headers.get('X-Total-Count'));
        if (Number.isFinite(headerTotal) && headerTotal > 0) {
            total = headerTotal; // il server è la fonte di verità (cache feed può variare)
        }
        const html = await response.text();
        const before = track.children.length;
        // Frammento HTML del NOSTRO endpoint same-origin (Blade-escapato server-side):
        // non è input utente raw — nessun rischio XSS da terze parti (CWE-79 n/a).
        track.insertAdjacentHTML('beforeend', html);
        return track.children.length - before;
    }

    /** Garantisce nel DOM le card necessarie alla pagina target (fetch sequenziali, uno in volo). */
    async function ensureCardsFor(targetPage: number): Promise<boolean> {
        const needed = Math.min(total, (targetPage + 1) * perPage);
        if (track.children.length >= needed) {
            return true;
        }
        busy = true;
        track.setAttribute('aria-busy', 'true');
        updateUI();
        clearStatus();
        try {
            while (track.children.length < needed) {
                const added = await fetchPage(track.children.length, Math.min(perPage, 12)); // CEO: min(N,12)
                if (added === 0) {
                    total = track.children.length; // slice vuota: il feed è più corto del dichiarato
                    renderDots();
                    break;
                }
            }
            return true;
        } catch {
            showError(targetPage); // feedback visibile + retry — mai console-only
            return false;
        } finally {
            busy = false;
            track.removeAttribute('aria-busy');
            updateUI();
        }
    }

    async function goToPage(target: number): Promise<void> {
        if (busy) {
            return; // un solo fetch in volo: input ignorato finché non torna
        }
        const clamped = Math.min(pageCount() - 1, Math.max(0, target));
        if (!(await ensureCardsFor(clamped))) {
            return; // errore già notificato; il retry ri-invoca goToPage(clamped)
        }
        track.scrollTo({
            left: Math.min(clamped * pageWidth(), maxScroll()),
            behavior: reducedMotion.matches ? 'auto' : 'smooth', // WCAG 2.2 §2.3.3 (AAA, best effort)
        });
    }

    function step(delta: number): void {
        measure(); // N sempre fresco (sezione può essere appena diventata visibile)
        void goToPage(currentPage() + delta);
    }

    prev.addEventListener('click', (event) => {
        event.preventDefault();
        step(-1);
    });
    next.addEventListener('click', (event) => {
        event.preventDefault();
        step(1);
    });

    let scrollTick = false;
    track.addEventListener(
        'scroll',
        () => {
            if (scrollTick) {
                return;
            }
            scrollTick = true;
            requestAnimationFrame(() => {
                scrollTick = false;
                updateUI();
            });
        },
        { passive: true },
    );

    let resizeTimer: number | undefined;
    window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            const previousPerPage = perPage;
            measure();
            if (perPage !== previousPerPage) {
                renderDots(); // pallini = ceil(total/N) col nuovo N
            } else {
                updateUI();
            }
        }, 150);
    });

    measure();
    renderDots();
}

function boot(): void {
    const sections = document.querySelectorAll<HTMLElement>('[data-carousel-section]');
    if (sections.length === 0) {
        return; // flag HOME_LAZY_SECTIONS OFF → nessun data-attr → no-op totale
    }
    const labels = readLabels();
    if (!labels) {
        return; // senza i18n il contratto errore+retry non è soddisfacibile → isola spenta
    }
    sections.forEach((section) => initCarousel(section, labels));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
    boot();
}

export {};
